<template>
  <div class="task-budget-content">
    <!-- Header -->
    <h2 class="task-title">
      <i class="pi pi-file-o"></i>
      {{ $t('reports.taskBudgetReport') }}
    </h2>

    <!-- Filters -->
    <div class="task-filters">
      <div class="filter-item">
        <label class="filter-label">
          <i class="pi pi-users"></i>
          {{ $t('reports.filterByIntervenant') }}
        </label>
        <CustomDropdown
          v-model="selectedIntervenant"
          :options="intervenantOptions"
          optionLabel="name"
          optionValue="id"
          :placeholder="$t('reports.selectIntervenant')"
          @change="applyFilters"
          showClear
        />
      </div>
    </div>

    <!-- Task Table -->
    <DataTable
      :key="tableKey"
      :value="paginatedTasks"
      sortMode="multiple"
      removableSort
      :loading="loading"
      responsiveLayout="scroll"
      class="task-table"
      :globalFilterFields="['taskName', 'intervenantName']"
    >
        <!-- Task Name Column -->
        <Column field="taskName" :header="$t('tasks.name')" sortable>
          <template #body="{ data }">
            <div class="task-info">
              <div class="task-name-wrapper">
                <span class="task-name" @click="$emit('task-click', data)">
                  <i class="pi pi-file-o task-icon"></i>
                  {{ data.taskName }}
                </span>
              </div>
              <small v-if="data.taskDescription" class="task-description">
                {{ data.taskDescription }}
              </small>
            </div>
          </template>
        </Column>

        <!-- Budget Amount Column -->
        <Column field="budgetAmount" :header="$t('reports.budget.allocated')" sortable>
          <template #body="{ data }">
            <div class="budget-cell">
              <span class="currency-amount positive-amount budget-amount">
                {{ formattedBudgets[data.taskId] || data.budgetAmount || '0' }}
                <small style="color: gray; font-size: 10px;" v-if="!formattedBudgets[data.taskId]">(raw)</small>
              </span>
              <div class="budget-indicator allocated"></div>
            </div>
          </template>
        </Column>

        <!-- Consumed Amount Column -->
        <Column field="consumedAmount" :header="$t('reports.consumed')" sortable>
          <template #body="{ data }">
            <div class="budget-cell">
              <span class="currency-amount negative-amount consumed-amount">
                {{ formattedConsumed[data.taskId] || data.consumedAmount || '0' }}
                <small style="color: gray; font-size: 10px;" v-if="!formattedConsumed[data.taskId]">(raw)</small>
              </span>
              <div class="budget-indicator consumed"></div>
            </div>
          </template>
        </Column>

        <!-- Remaining Amount Column -->
        <Column field="remainingAmount" :header="$t('reports.remaining')" sortable>
          <template #body="{ data }">
            <div class="budget-cell">
              <span 
                :class="[
                  'currency-amount',
                  'remaining-amount',
                  data.remainingAmount >= 0 ? 'positive-amount' : 'negative-amount'
                ]"
              >
                {{ formattedRemaining[data.taskId] || data.remainingAmount || '0' }}
                <small style="color: gray; font-size: 10px;" v-if="!formattedRemaining[data.taskId]">(raw)</small>
              </span>
              <div 
                class="budget-indicator remaining"
                :class="{ 'negative': data.remainingAmount < 0 }"
              ></div>
            </div>
          </template>
        </Column>

        <!-- Consumption Percentage Column -->
        <Column field="consumptionPercentage" :header="$t('reports.consumptionPercentage')" sortable>
          <template #body="{ data }">
            <div class="consumption-cell">
              <ProgressBar 
                :value="Math.min(data.consumptionPercentage, 100)"
                :class="getProgressBarClass(data.consumptionPercentage)"
                class="consumption-bar modern-progress"
              />
              <span 
                class="consumption-text"
                :class="getPercentageClass(data.consumptionPercentage)"
              >
                {{ formatPercentage(data.consumptionPercentage) }}
              </span>
              <div v-if="data.consumptionPercentage > 100" class="over-consumption-indicator">
                <i class="pi pi-exclamation-triangle"></i>
                +{{ formatPercentage(data.consumptionPercentage - 100) }}
              </div>
            </div>
          </template>        </Column>

        <!-- Intervenant Column -->
        <Column field="intervenantName" :header="$t('intervenants.name')" sortable>
          <template #body="{ data }">
            <div class="intervenant-cell">
              <Avatar 
                v-if="data.intervenantName"
                :label="getInitials(data.intervenantName)"
                shape="circle"
                size="small"
                class="intervenant-avatar"
              />
              <span 
                v-if="data.intervenantName"
                class="intervenant-name"
                @click="$emit('intervenant-click', { id: data.intervenantId, name: data.intervenantName })"
              >
                {{ data.intervenantName }}
              </span>
              <span v-else class="no-intervenant">
                <i class="pi pi-user-minus"></i>
                {{ $t('reports.notAssigned') }}
              </span>
            </div>
          </template>
        </Column>

        <!-- Dates Column -->
        <Column :header="$t('reports.dates')" sortable>
          <template #body="{ data }">
            <div class="dates-info">
              <div class="date-item start-date">
                <i class="pi pi-calendar-plus"></i>
                <div class="date-content">
                  <small>{{ $t('tasks.startDate') }}</small>
                  <span>{{ formatDate(data.startDate) || '-' }}</span>
                </div>
              </div>
              <div class="date-item end-date">
                <i class="pi pi-calendar-minus"></i>
                <div class="date-content">
                  <small>{{ $t('tasks.endDate') }}</small>
                  <span>{{ formatDate(data.endDate) || '-' }}</span>
                </div>
              </div>
            </div>
          </template>
        </Column>

        <!-- Expenses Column -->
        <Column :header="$t('reports.expenses')" sortable>
          <template #body="{ data }">
            <div class="expenses-info">
              <div class="expense-count">
                <i class="pi pi-receipt"></i>
                <span class="count">{{ data.expenseCount }}</span>
                <span class="label">{{ $t('reports.expenses') }}</span>
              </div>
              <div v-if="data.firstExpenseDate" class="expense-dates">
                <i class="pi pi-clock"></i>
                <small>{{ formatDate(data.firstExpenseDate) }} - {{ formatDate(data.lastExpenseDate) }}</small>
              </div>
            </div>
          </template>
        </Column>
      </DataTable>

    <!-- Pagination Controls -->
    <PaginationControls
      v-model:currentPage="currentPage"
      :pageSize="pageSize"
      :totalItems="filteredTasks.length"
      :itemsLabel="$t('reports.tasks.title').toLowerCase()"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDate, formatPercentage } from '@/utils/formatters';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ProgressBar from 'primevue/progressbar';
import Avatar from 'primevue/avatar';
import CustomDropdown from '@/components/common/CustomDropdown.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
// @ts-ignore
import { ref, computed, watch, nextTick } from 'vue';

interface Props {
  tasks?: any[];
  loading?: boolean;
}

const props = defineProps<Props>();

defineEmits(['task-click', 'intervenant-click']);

// State
const selectedIntervenant = ref<number | null>(null);
const formattedBudgets = ref<Record<string, string>>({} as Record<string, string>);
const formattedConsumed = ref<Record<string, string>>({} as Record<string, string>);
const formattedRemaining = ref<Record<string, string>>({} as Record<string, string>);
const tableKey = ref<number>(0); // For forcing re-render

// Pagination state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);

// Format currency amounts when tasks change
const formatTaskAmounts = async () => {
  if (!props.tasks?.length) return;

  const budgets: Record<string, string> = {};
  const consumed: Record<string, string> = {};
  const remaining: Record<string, string> = {};

  for (const task of props.tasks) {
    if (task.taskId) {
      try {
        budgets[String(task.taskId)] = await formatCurrencyWithUserPreference(task.budgetAmount);
        consumed[String(task.taskId)] = await formatCurrencyWithUserPreference(task.consumedAmount);
        remaining[String(task.taskId)] = await formatCurrencyWithUserPreference(task.remainingAmount);
      } catch (err) {
        console.warn('Error formatting currency for task:', task.taskId, err);
        budgets[String(task.taskId)] = task.budgetAmount?.toString() || '0';
        consumed[String(task.taskId)] = task.consumedAmount?.toString() || '0';
        remaining[String(task.taskId)] = task.remainingAmount?.toString() || '0';
      }
    }
  }

  formattedBudgets.value = budgets;
  formattedConsumed.value = consumed;
  formattedRemaining.value = remaining;
  
  // Force table re-render to update currency display
  await nextTick();
  tableKey.value++;
};

// Watch for tasks changes
watch(() => props.tasks, formatTaskAmounts, { immediate: true, deep: true });

// Computed properties
const intervenantOptions = computed(() => {
  const intervenants = [...new Set((props.tasks || [])
    .filter((task: any) => task.intervenantName)
    .map((task: any) => ({ id: task.intervenantId, name: task.intervenantName }))
  )];
  return intervenants.filter((intervenant: any, index: number, self: any[]) => 
    index === self.findIndex((i: any) => i.id === intervenant.id)
  );
});

const filteredTasks = computed(() => {
  let filtered = [...(props.tasks || [])];

  if (selectedIntervenant.value) {
    filtered = filtered.filter((task: any) => task.intervenantId === selectedIntervenant.value);
  }

  return filtered;
});

const paginatedTasks = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredTasks.value.slice(start, end);
});

// Methods
const applyFilters = () => {
  // Reset to first page when filters change
  currentPage.value = 1;
};

const getProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'progress-over';
  if (percentage >= 80) return 'progress-high';
  if (percentage >= 60) return 'progress-medium';
  return 'progress-low';
};

const getPercentageClass = (percentage: number) => {
  if (percentage >= 100) return 'over-budget';
  if (percentage >= 80) return 'high-usage';
  if (percentage >= 60) return 'medium-usage';
  return 'low-usage';
};

const getInitials = (name: string) => {
  return name.split(' ').map((word: string) => word.charAt(0)).join('').toUpperCase().substring(0, 2);
};
</script>

<style scoped>
/* Task Budget Content */
.task-budget-content {
  padding: var(--space-4);
}

/* Header */
.task-title {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0 0 var(--space-6) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-800);
}

.task-title i {
  color: var(--primary);
  font-size: var(--font-size-xl);
}

/* Filters */
.task-filters {
  margin-bottom: var(--space-6);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-width: 300px;
}

.filter-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-700);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.filter-label i {
  color: var(--primary);
  font-size: var(--font-size-sm);
}

/* Table Styling */
:deep(.task-table) {
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
  overflow: hidden;
}

:deep(.task-table .p-datatable-thead > tr > th) {
  background: var(--neutral-50);
  border-bottom: 1px solid var(--neutral-200);
  color: var(--neutral-700);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  padding: var(--space-3);
}

:deep(.task-table .p-datatable-tbody > tr) {
  transition: background-color var(--transition-base);
}

:deep(.task-table .p-datatable-tbody > tr:hover) {
  background: var(--neutral-50);
}

:deep(.task-table .p-datatable-tbody > tr > td) {
  border-bottom: 1px solid var(--neutral-100);
  padding: var(--space-3);
}

/* Task Info */
.task-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.task-name {
  font-weight: var(--font-weight-semibold);
  color: var(--primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  transition: color var(--transition-base);
}

.task-name:hover {
  color: var(--primary-dark);
}

.task-icon {
  color: var(--primary);
  font-size: var(--font-size-sm);
}

.task-description {
  color: var(--neutral-600);
  font-size: var(--font-size-sm);
}

/* Budget Cells */
.budget-cell {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Universal currency amount styling */
.currency-amount {
  font-weight: 700;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.currency-amount.positive-amount {
  color: #059669;
}

.currency-amount.negative-amount {
  color: #dc2626;
}

/* Specific styling for budget amounts */
.budget-amount,
.consumed-amount,
.remaining-amount {
  font-weight: 700;
}

.budget-amount {
  color: #059669; /* Green for budget (positive) */
}

.consumed-amount {
  color: #dc2626; /* Red for consumed (negative/expense) */
}

.remaining-amount {
  color: #059669; /* Green for positive remaining */
}

.remaining-amount.negative-amount {
  color: #dc2626; /* Red for negative remaining */
}

.budget-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.budget-indicator.allocated {
  background: var(--info);
}

.budget-indicator.consumed {
  background: var(--warning);
}

.budget-indicator.remaining {
  background: var(--success);
}

.budget-indicator.remaining.negative {
  background: var(--error);
}

/* Consumption Cell */
.consumption-cell {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 120px;
}

:deep(.modern-progress) {
  height: 8px;
  border-radius: var(--radius-sm);
  background: var(--neutral-200);
}

:deep(.modern-progress .p-progressbar-value) {
  border-radius: var(--radius-sm);
  transition: all var(--transition-base);
}

:deep(.progress-over .p-progressbar-value) {
  background: var(--error);
}

:deep(.progress-high .p-progressbar-value) {
  background: var(--warning);
}

:deep(.progress-medium .p-progressbar-value) {
  background: var(--info);
}

:deep(.progress-low .p-progressbar-value) {
  background: var(--success);
}

.consumption-text {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-align: center;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--neutral-50);
}

.consumption-text.over-budget {
  color: var(--error);
  background: rgba(var(--error-rgb), 0.1);
}

.consumption-text.high-usage {
  color: var(--warning);
  background: rgba(var(--warning-rgb), 0.1);
}

.consumption-text.medium-usage {
  color: var(--info);
  background: rgba(var(--info-rgb), 0.1);
}

.consumption-text.low-usage {
  color: var(--success);
  background: rgba(var(--success-rgb), 0.1);
}

.over-consumption-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--error);
  background: rgba(var(--error-rgb), 0.1);
  border: 1px solid rgba(var(--error-rgb), 0.2);
  border-radius: var(--radius-sm);
  padding: var(--space-1) var(--space-2);
}

/* Intervenant Cell */
.intervenant-cell {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

:deep(.intervenant-avatar) {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  background: var(--primary);
}

.intervenant-name {
  color: var(--primary);
  cursor: pointer;
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-base);
}

.intervenant-name:hover {
  color: var(--primary-dark);
}

.no-intervenant {
  color: var(--neutral-500);
  font-style: italic;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
}

/* Dates Info */
.dates-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
}

.date-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--neutral-50);
}

.date-item i {
  color: var(--primary);
  font-size: var(--font-size-xs);
  width: 12px;
}

.date-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.date-content small {
  color: var(--neutral-500);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
}

.date-content span {
  color: var(--neutral-700);
  font-weight: var(--font-weight-medium);
}

/* Expenses Info */
.expenses-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.expense-count {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  padding: var(--space-2) var(--space-3);
  background: rgba(var(--warning-rgb), 0.1);
  border-radius: var(--radius-sm);
  border: 1px solid rgba(var(--warning-rgb), 0.2);
}

.expense-count i {
  color: var(--warning);
  font-size: var(--font-size-sm);
}

.expense-count .count {
  font-weight: var(--font-weight-bold);
  color: var(--neutral-800);
}

.expense-count .label {
  color: var(--neutral-600);
  font-size: var(--font-size-xs);
}

.expense-dates {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-xs);
  color: var(--neutral-500);
  padding: var(--space-1) var(--space-2);
  background: var(--neutral-50);
  border-radius: var(--radius-sm);
}

.expense-dates i {
  color: var(--primary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .task-budget-content {
    padding: var(--space-3);
  }

  .task-title {
    font-size: var(--font-size-lg);
  }

  .filter-item {
    max-width: 100%;
  }

  :deep(.task-table .p-datatable-thead > tr > th),
  :deep(.task-table .p-datatable-tbody > tr > td) {
    padding: var(--space-2);
  }

  .consumption-cell {
    min-width: 100px;
  }
}

@media (max-width: 480px) {
  .task-budget-content {
    padding: var(--space-2);
  }

  .task-title {
    font-size: var(--font-size-base);
    flex-direction: column;
    gap: var(--space-2);
  }
}
</style>
